import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { CardSlot } from '@/components/dashboard/DashboardCard'
import AboutThisData from '@/components/market/AboutThisData'
import AttentionList from '@/components/market/AttentionList'
import CompetitorsTable from '@/components/market/CompetitorsTable'
import MarketEmptyState from '@/components/market/MarketEmptyState'
import MarketHeader from '@/components/market/MarketHeader'
import MarketMoves from '@/components/market/MarketMoves'
import NightGrid from '@/components/market/NightGrid'
import PositionCard from '@/components/market/PositionCard'
import SummaryStrip from '@/components/market/SummaryStrip'
import ValueMap from '@/components/market/ValueMap'
import {
  attentionTones,
  badgeNumbers,
  initialNightDate,
  replaceNightParam,
  useIsDesktop,
  usePrefersReducedMotion,
} from '@/lib/market'
import type { MarketDashboardProps, MarketPriceDomain } from '@/types/market'

/**
 * MARKET INTELLIGENCE, OVERVIEW. Where your price sits against your competitors' list prices on
 * Booking.com, night by night, and what changed since the last check.
 *
 * Composed entirely server-side (BuildMarketDashboardMutation): this page prints what it is given
 * and computes nothing. It owns one piece of state, the selected night, which every section can set
 * and which is reflected in `?night=` (history replaced, never a visit). It never changes a price;
 * its actions open the Prices calendar or the Events page.
 */

/** Only for a ready page with nothing to plot: dots and ticks have nowhere to go, and none are drawn. */
const NO_DOMAIN: MarketPriceDomain = { min: 0, max: 1, step: 1, ticks: [] }

export default function Dashboard({ currentUser, property, properties, market }: MarketDashboardProps) {
  const isDesktop = useIsDesktop()
  const reducedMotion = usePrefersReducedMotion()
  const { nights, attention: attentionData, competitors } = market

  const attention = useMemo(() => attentionTones(nights, attentionData.items), [nights, attentionData.items])
  const numbers = useMemo(() => badgeNumbers(competitors), [competitors])
  const domain = market.priceDomain ?? NO_DOMAIN

  const [selectedDate, setSelectedDate] = useState<string | null>(() =>
    initialNightDate(nights, attentionData.items, typeof window === 'undefined' ? '' : window.location.search)
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  const positionRef = useRef<HTMLDivElement>(null)

  // A reload (after a check) can drop the selected night off the front of the list.
  useEffect(() => {
    if (selectedDate && nights.some((n) => n.date === selectedDate)) return
    setSelectedDate(initialNightDate(nights, attentionData.items, ''))
  }, [nights, attentionData.items, selectedDate])

  const selectNight = useCallback((date: string) => {
    setSelectedDate(date)
    replaceNightParam(date)
  }, [])

  // From a card, a move or a grid row: select it AND bring it into view (the sheet, on a phone).
  const revealNight = useCallback(
    (date: string) => {
      selectNight(date)
      if (!isDesktop) {
        setSheetOpen(true)
        return
      }
      const el = positionRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const visible = rect.top >= 0 && rect.top < window.innerHeight * 0.5
      if (!visible) el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' })
    },
    [isDesktop, reducedMotion, selectNight]
  )

  return (
    <OperatorLayout title="Market Intelligence" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <MarketHeader
          propertyBasePath={property.basePath}
          comparison={market.comparison}
          freshness={market.freshness}
          marketPositionRule={market.marketPositionRule}
          canManage={market.canManage}
        />

        {market.status !== 'ready' ? (
          market.emptyState && <MarketEmptyState status={market.status} emptyState={market.emptyState} />
        ) : (
          <>
            {market.tiles.length > 0 && (
              <CardSlot delay={100}>
                <SummaryStrip tiles={market.tiles} windowNights={market.windowNights} />
              </CardSlot>
            )}

            {nights.length > 0 && (
              <CardSlot delay={150}>
                <div ref={positionRef} id="position" className="scroll-mt-6">
                  <PositionCard
                    market={market}
                    domain={domain}
                    selectedDate={selectedDate}
                    onSelect={selectNight}
                    attention={attention}
                    numbers={numbers}
                    isDesktop={isDesktop}
                    reducedMotion={reducedMotion}
                    sheetOpen={sheetOpen}
                    onSheetOpenChange={setSheetOpen}
                  />
                </div>
              </CardSlot>
            )}

            <CardSlot delay={200}>
              <AttentionList attention={attentionData} selectedDate={selectedDate} onShow={revealNight} />
            </CardSlot>

            <CardSlot delay={250}>
              <MarketMoves
                moves={market.moves}
                competitors={competitors}
                numbers={numbers}
                nights={nights}
                currency={market.currency}
                onShow={revealNight}
              />
            </CardSlot>

            {isDesktop && nights.length > 0 && (
              <CardSlot delay={300}>
                <NightGrid
                  nights={nights}
                  competitors={competitors}
                  numbers={numbers}
                  attention={attention}
                  currency={market.currency}
                  domain={domain}
                  today={market.today}
                  selectedDate={selectedDate}
                  onSelect={revealNight}
                />
              </CardSlot>
            )}

            <CardSlot delay={350}>
              <CompetitorsTable
                competitors={competitors}
                you={market.you}
                numbers={numbers}
                currency={market.currency}
                windowNights={market.windowNights}
                compsetHref={market.links.compset}
                canManage={market.canManage}
              />
            </CardSlot>

            <CardSlot delay={400}>
              <ValueMap
                valueMap={market.valueMap}
                numbers={numbers}
                currency={market.currency}
                domain={market.priceDomain}
                reducedMotion={reducedMotion}
              />
            </CardSlot>

            <CardSlot delay={450}>
              <AboutThisData
                freshness={market.freshness}
                comparison={market.comparison}
                listing={market.listing}
                marketPositionRule={market.marketPositionRule}
                compsetHref={market.links.compset}
                canManage={market.canManage}
              />
            </CardSlot>
          </>
        )}
      </div>
    </OperatorLayout>
  )
}
